import React from 'react';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/homepage/i18n/en';
import { FeatureContent } from '@gik/marketing';
import { IFeatureContent } from '@gik/marketing/components/FeatureContent';
import { Animation, Container, HTMLParser, IPageSectionProps, PageSection } from '@gik/ui';
import animationCalendar from '../../assets/calendar/cal.json';
import animationWishlist from '../../assets/wishlist/wishlist.json';
import animationUpdates from '../../assets/updates/updates.json';
import animationFundraising from '../../assets/fundraising/fundraising.json';
import bemBlock from '@gik/core/utils/bemBlock';
import {
  CalendarTestimonial,
  FundraisingTestimonial,
  UpdatesTestimonial,
  WishlistTestimonial,
} from '@gik/homepage/testimonials';
import FeatureTile, { FeatureTileProps } from '@gik/homepage/components/FeatureTile/FeatureTile';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import { onScrollMode } from '@gik/ui/Animation/Animation';
import {
  calendarNavId,
  donationsNavId,
  updatesNavId,
  wishlistNavId,
} from '@gik/marketing/components/FeaturesNav/FeaturesNav';

const blockName = 'features';

export default function Features() {
  const bem = bemBlock(blockName);
  const featuresPageHref = '/features';

  const content: Array<
    IFeatureContent &
      FeatureTileProps &
      Pick<IPageSectionProps, 'decoration'> &
      Pick<IPageSectionProps, 'decorationPlacement'> &
      Pick<HTMLElement, 'id'>
  > = [
    {
      id: 'care-calendar',
      variant: 'primary',
      title: i18n.t(translationKeys.titleCalendar),
      description: <HTMLParser rawHtml={i18n.t(translationKeys.descriptionCalendar)} />,
      testimonial: CalendarTestimonial,
      linkLabel: i18n.t(translationKeys.linkCalendar),
      linkIcon: `${getAssetsUrl()}marketing/icons/calendar.png`,
      linkRef: `${featuresPageHref}#${calendarNavId}`,
      decoration: 'foliage-pointy',
      decorationPlacement: 'left',
      aside: (
        <Animation
          className={bem('animation')}
          data={animationCalendar}
          mode={onScrollMode}
          assetsPrepend={`${getAssetsUrl()}homepage/calendar/images/`}
        />
      ),
    },
    {
      id: 'wishlist',
      variant: 'danger',
      title: i18n.t(translationKeys.titleWishlist),
      description: <HTMLParser rawHtml={i18n.t(translationKeys.descriptionWishlist)} />,
      testimonial: WishlistTestimonial,
      linkLabel: i18n.t(translationKeys.linkWishlist),
      linkIcon: `${getAssetsUrl()}marketing/icons/wishlist.png`,
      linkRef: `${featuresPageHref}#${wishlistNavId}`,
      decoration: 'flowers',
      decorationPlacement: 'right',
      aside: (
        <Animation
          className={bem('animation')}
          data={animationWishlist}
          mode={onScrollMode}
          assetsPrepend={`${getAssetsUrl()}homepage/wishlist/images/`}
          intersectionRatioThreshold={0.6}
        />
      ),
      reversed: true,
    },
    {
      id: 'updates',
      variant: 'secondary',
      title: i18n.t(translationKeys.titleUpdates),
      description: <HTMLParser rawHtml={i18n.t(translationKeys.descriptionUpdates)} />,
      testimonial: UpdatesTestimonial,
      linkLabel: i18n.t(translationKeys.linkUpdates),
      linkIcon: `${getAssetsUrl()}marketing/icons/updates.png`,
      linkRef: `${featuresPageHref}#${updatesNavId}`,
      decoration: 'foliage-round',
      decorationPlacement: 'left',
      aside: (
        <Animation
          className={bem('animation')}
          data={animationUpdates}
          mode={onScrollMode}
          assetsPrepend={`${getAssetsUrl()}homepage/updates/images/`}
          intersectionRatioThreshold={0.6}
        />
      ),
    },
    {
      id: 'donations',
      variant: 'success',
      title: i18n.t(translationKeys.titleFundraising),
      description: <HTMLParser rawHtml={i18n.t(translationKeys.descriptionFundraising)} />,
      testimonial: FundraisingTestimonial,
      linkLabel: i18n.t(translationKeys.linkFundraising),
      linkIcon: `${getAssetsUrl()}marketing/icons/fundraising.png`,
      linkRef: `${featuresPageHref}#${donationsNavId}`,
      decoration: 'foliage-small',
      decorationPlacement: 'right',
      aside: (
        <Animation
          className={bem('animation')}
          data={animationFundraising}
          mode={onScrollMode}
          assetsPrepend={`${getAssetsUrl()}homepage/fundraising/images/`}
          intersectionRatioThreshold={0.6}
        />
      ),
      reversed: true,
    },
  ];

  return (
    <section className={bem()}>
      <Container>
        <h2 className={bem('copy-separator')}>{i18n.t(translationKeys.lifeIsFullOfUnknowns)}</h2>
      </Container>

      {content.map((props, i) => (
        <PageSection
          key={i}
          id={bem(props.id)}
          className={bem(props.id)}
          decoration={props.decoration}
          decorationPlacement={props.decorationPlacement}
          noPad
        >
          <FeatureContent {...props}>
            <FeatureTile {...props} />
          </FeatureContent>
        </PageSection>
      ))}
    </section>
  );
}
